import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Thermometer, Flame, TrendingUp, Clock, Ban } from "lucide-react";

import {
  multiplierToIncreasePct,
  DiveDeepChartMetric,
  SensitivitySummaryRow,
} from "./heatwaveChartShared";

const colors = {
  textDark: "#0f2744",
  textMuted: "#5a7a94",
  cardShadow: "rgba(74, 159, 212, 0.15)",
  orange500: "#F97316",
  yellow400: "#FACC15",
  red500: "#EF4444",
};

const KpiGridWrapper = styled.div`
  margin-bottom: 2.25rem;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const KpiItemCard = styled.div<{ $highlighted?: boolean }>`
  background: ${({ $highlighted }) =>
    $highlighted
      ? `linear-gradient(135deg, ${colors.yellow400} 0%, ${colors.orange500} 55%, ${colors.red500} 100%)`
      : "rgba(255, 255, 255, 0.82)"};
  border: ${({ $highlighted }) =>
    $highlighted ? "none" : "1px solid rgba(255, 255, 255, 0.95)"};
  border-radius: 1rem;
  padding: 1rem;
  text-align: center;
  backdrop-filter: blur(8px);
  box-shadow: ${({ $highlighted }) =>
    $highlighted
      ? "0 4px 20px rgba(249, 115, 22, 0.45)"
      : `0 4px 16px ${colors.cardShadow}`};
  min-width: 0;
`;

const KpiValue = styled.div<{ $highlighted?: boolean }>`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ $highlighted }) => ($highlighted ? "white" : colors.textDark)};
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 1.375rem;
  }
`;

const KpiValueRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  margin-bottom: 0.35rem;
`;

const KpiLabel = styled.div<{ $highlighted?: boolean }>`
  font-size: 0.75rem;
  color: ${({ $highlighted }) =>
    $highlighted ? "rgba(255,255,255,0.88)" : colors.textMuted};
  line-height: 1.35;

  @media (min-width: 768px) {
    font-size: 0.8125rem;
  }
`;

type KpiItem = {
  labelKey: string;
  value: string;
  highlighted?: boolean;
  icon: React.ReactNode;
};

function formatDecimal(value: number, locale: string, fractionDigits = 1): string {
  return value.toLocaleString(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatDelayMinutesFromSeconds(seconds: number, locale: string): string {
  return `${formatDecimal(seconds / 60, locale)} min`;
}

function formatPct(value: number, locale: string): string {
  return `${formatDecimal(value, locale)}%`;
}

function getHourUnit(locale: string): string {
  return locale.startsWith("nl") ? "u" : "h";
}

function formatHours(value: number, locale: string): string {
  const formatted = Math.round(value).toLocaleString(locale, { useGrouping: false });
  return `${formatted} ${getHourUnit(locale)}`;
}

function formatCount(value: number, locale: string): string {
  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
}

function buildDelayKpis(row: SensitivitySummaryRow, locale: string): KpiItem[] {
  return [
    {
      labelKey: "delayBelow30",
      value: formatDelayMinutesFromSeconds(row.delay_below_30_s, locale),
      icon: <Thermometer size={22} color="#4A9FD4" />,
    },
    {
      labelKey: "delayAbove30",
      value: formatDelayMinutesFromSeconds(row.delay_above_30_s, locale),
      icon: <Flame size={22} color="#F97316" />,
    },
    {
      labelKey: "delayMultiplier",
      value: `+${multiplierToIncreasePct(row.delay_multiplier)}%`,
      icon: <TrendingUp size={22} color="#5a7a94" />,
    },
    {
      labelKey: "extraDelayPerHotDay",
      value: formatHours(row.extra_delay_per_average_hot_day, locale),
      highlighted: true,
      icon: <Clock size={22} color="white" />,
    },
  ];
}

function buildCancellationKpis(row: SensitivitySummaryRow, locale: string): KpiItem[] {
  return [
    {
      labelKey: "cancelBelow30",
      value:
        row.cancel_below_30_pct != null ? formatPct(row.cancel_below_30_pct, locale) : "—",
      icon: <Thermometer size={22} color="#4A9FD4" />,
    },
    {
      labelKey: "cancelAbove30",
      value:
        row.cancel_above_30_pct != null ? formatPct(row.cancel_above_30_pct, locale) : "—",
      icon: <Flame size={22} color="#F97316" />,
    },
    {
      labelKey: "cancelMultiplier",
      value:
        row.cancel_multiplier != null
          ? `+${multiplierToIncreasePct(row.cancel_multiplier)}%`
          : "—",
      icon: <TrendingUp size={22} color="#5a7a94" />,
    },
    {
      labelKey: "extraCancelledTrains",
      value:
        row.extra_cancelled_stopovers != null &&
        row.avg_total_records > 0
          ? formatCount(
              Math.round(
                row.extra_cancelled_stopovers * (row.avg_trips_per_day / row.avg_total_records)
              ),
              locale
            )
          : "—",
      highlighted: true,
      icon: <Ban size={22} color="white" />,
    },
  ];
}

function KpiRow({ items }: { items: KpiItem[] }) {
  const { t } = useTranslation();

  return (
    <KpiGrid>
      {items.map(({ labelKey, value, highlighted, icon }) => (
        <KpiItemCard key={labelKey} $highlighted={highlighted}>
          <KpiValueRow>
            {icon}
            <KpiValue $highlighted={highlighted}>{value}</KpiValue>
          </KpiValueRow>
          <KpiLabel $highlighted={highlighted}>{t(`heatwave.diveDeep.kpis.${labelKey}`)}</KpiLabel>
        </KpiItemCard>
      ))}
    </KpiGrid>
  );
}

interface HeatwaveDiveDeepKpisProps {
  summaryRow?: SensitivitySummaryRow;
  metric: DiveDeepChartMetric;
}

const HeatwaveDiveDeepKpis: React.FC<HeatwaveDiveDeepKpisProps> = ({
  summaryRow,
  metric,
}) => {
  const { i18n } = useTranslation();

  if (!summaryRow) return null;

  const isDelay = metric === "delay";
  const locale = i18n.language;

  return (
    <KpiGridWrapper>
      <KpiRow
        items={
          isDelay
            ? buildDelayKpis(summaryRow, locale)
            : buildCancellationKpis(summaryRow, locale)
        }
      />
    </KpiGridWrapper>
  );
};

export default HeatwaveDiveDeepKpis;
